/**
 * "Received — not in the fund yet" (0084). A client can send money on any day; the
 * fund takes it on its cut-off date, and the term and the returns start THEN. Without
 * this card the money would be invisible between the day it arrived and the day it
 * starts counting, which reads as "my transfer got lost".
 *
 * Informational: no action, no urgency styling. Hidden when there is nothing waiting.
 */
import { formatMoney } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import type { EnteringFund } from '../../types/finance';
import { enteringFundLine } from './enteringFund.helpers';

export default function EnteringFundCard({ rows }: Readonly<{ rows: EnteringFund[] }>): JSX.Element | null {
  if (rows.length === 0) return null;
  const hasDates = rows.some((row) => !row.awaiting_allocation);
  return (
    <section
      aria-label="Money received that has not been applied to a series yet"
      className="rounded-lg p-4 mb-4"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <h2 className="text-sm font-semibold mb-1" style={{ color: colors.ink }}>
        Received — waiting to start
      </h2>
      {hasDates && (
        <p className="text-xs mb-3" style={{ color: colors.inkSoft }}>
          Each series takes new money on its own schedule. These dates are set by the
          series and can change.
        </p>
      )}
      <ul className="space-y-2">
        {rows.map((row) => (
          <li key={row.deposit_request_id} className="text-sm" style={{ color: colors.ink }}>
            {row.awaiting_allocation ? (
              <span className="font-semibold">{row.product_name}</span>
            ) : (
              <>
                <span className="font-semibold">{formatMoney(row.amount, row.currency_code)}</span>
                {' · '}{row.product_name}
              </>
            )}
            <div className="text-xs" style={{ color: colors.inkSoft }}>{enteringFundLine(row)}</div>
          </li>
        ))}
      </ul>
    </section>
  );
}
